/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import moment from 'moment';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import ExtendProjectForm from './ExtendProjectForm/ExtendProjectForm';
import ExtendProjectSuccess from './ExtendProjectSuccess/ExtendProjectSuccess';
import ExtendProjectError from './ExtendProjectError/ExtendProjectError';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  project: Object,
  actionDrawerStore: TypeActionDrawerStore,
  projectDetailsStore: Object,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'projectDetailsStore')
@observer
export default class ExtendProject extends React.Component<*> {
  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore, projectDetailsStore }: TypeProps = this.props;
    const project: Object = this.props.project;

    let currentEndDate: Date = new Date();

    if (project && project.end_date) {
      currentEndDate = moment(project.end_date, 'YYYY-MM-DD').toDate();
    }

    const fields: Array<Object> = [
      {
        name: 'extendProjectForm',
        label: t('general:actions.extend_project'),
        title: 'Extend Project',
        fields: [
          {
            name: 'reason',
            label: t('general:reasons.reason_for_extending_project'),
            rules: 'required|string',
            value: '',
            type: 'select',
            options: [
              {
                id: 1,
                label: t('general:reasons.options.project_not_complete'),
              },
              {
                id: 2,
                label: t('general:reasons.options.customer_extended_project_duration'),
              },
              {
                id: 'other',
                label: t('general:reasons.options.other'),
              },
            ],
          },
          {
            name: 'endDate',
            label: t('general:projects.labels.new_end_date'),
            rules: 'required|date|after:' + moment(currentEndDate).format('YYYY-MM-DD'),
            value: new Date(
              moment(currentEndDate)
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
        ],
      },
    ];

    const hooks: Object = {
      onInit(data: Object) {
        if (!(project && project.trading_account)) {
          projectDetailsStore.clearDetails();
          projectDetailsStore.loadProjectDetails(project.id);
        }
      },
      onSuccess(data: Object) {
        const { reason, endDate }: Object = data.values().extendProjectForm;

        actionDrawerStore.extendProject(project.id, {
          extend_reason: reason,
          end_date: endDate,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.extend_project'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const project: Object = this.props.project;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.extend_project')} />
          <ExtendProjectError response={actionDrawerStore.errorResponse} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={t('general:actions.extend_project')} />
          <ExtendProjectSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.extend_project')} />
        <ExtendProjectForm project={project} form={this.createForm()} />
      </div>
    );
  }
}
